import { useMutation } from "@tanstack/react-query";
import Swal from "sweetalert2";
import { api } from "../../libs/axios";
import { Pedido } from "../../utils/interfaces";
import { AxiosError } from "axios";

export type AxiosErrorResponse = AxiosError<{
  message?: string;
  code?: string;
  attempts_remaining?: number;
  order_cancelled?: boolean;
  redirect_to?: string;
  data?: {
    message?: string;
    code?: string;
    attempts_remaining?: number;
    order_cancelled?: boolean;
    redirect_to?: string;
    redirect_to_home?: boolean;
    error_type?: string;
  };
}>;

interface PoltronaReserva {
  origem: string,
  destino: string,
  cidadeOrigemId: string,
  cidadeDestinoId: string,
  data: string,
  servico: string,
  assento: string,
  grupo: string,
  viagem_premiada: boolean,
  identificador: string,
  classe: string,
  saida_hora: string,
  empresa: string,
  preco: string,
  precoBase?: number | string | null,
  precoSemViagemPremiada?: number | string | null,
  desembarque: string,
  adaptador: string
  viacao_id?: string | number;
  provedorWebviagem?: number | null;
  nome: string;
  documento: string;
  telefone: string;
  data_nascimento: string;
  contexto: string;
}

interface AvisoReserva {
  codigo?: string;
  mensagem?: string;
  bilhete_id?: string | number;
  assento?: string;
  valor_base_busca?: string | number;
  valor_base_reserva?: string | number;
  valor_busca?: string | number;
  valor_atual?: string | number;
  valor_reserva?: string | number;
}

const CODIGO_AVISO_DIVERGENCIA_VALOR = 'divergencia_valor_poltrona';
const VALOR_MINIMO_AUMENTO_AVISO = 1;

function deveExibirAvisoReserva(aviso: AvisoReserva): boolean {
  if (aviso.codigo !== CODIGO_AVISO_DIVERGENCIA_VALOR) {
    return true;
  }

  const valorOriginal = Number(aviso.valor_busca ?? aviso.valor_base_busca);
  const valorAtual = Number(aviso.valor_atual ?? aviso.valor_reserva ?? aviso.valor_base_reserva);

  return Number.isFinite(valorOriginal)
    && Number.isFinite(valorAtual)
    && Number((valorAtual - valorOriginal).toFixed(2)) >= VALOR_MINIMO_AUMENTO_AVISO;
}

export function extrairAvisosReserva(data: any): AvisoReserva[] {
  const avisos = Array.isArray(data?.avisos)
    ? data.avisos
    : (Array.isArray(data?.data?.avisos) ? data.data.avisos : []);

  return avisos.filter(deveExibirAvisoReserva);
}

function formatBRL(value: unknown): string {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "";
  }

  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(number);
}

export async function exibirAvisosReserva(avisos: AvisoReserva[]): Promise<"continuar" | "excluir" | null> {
  if (avisos.length === 0) {
    return null;
  }

  const aviso = avisos[0];
  const novoValor = formatBRL(aviso?.valor_atual ?? aviso?.valor_reserva);

  const result = await Swal.fire({
    title: "⚠️ Atenção!",
    html: `
      <div style="text-align:left">
        <p>O valor da poltrona selecionada foi alterado pela empresa de ônibus.</p>
        ${novoValor ? `<p><strong>Novo valor: ${novoValor}</strong></p>` : ""}
        <p>Se não concordar com o novo valor, exclua esta poltrona e selecione outra antes de continuar.</p>
      </div>
    `,
    showCancelButton: true,
    cancelButtonText: "Excluir poltrona",
    confirmButtonText: "Continuar",
    confirmButtonColor: "#dd6b20",
    cancelButtonColor: "#ffffff",
    reverseButtons: false,
    allowEscapeKey: false,
    allowOutsideClick: false,
    didOpen: () => {
      const cancelButton = Swal.getCancelButton();
      const confirmButton = Swal.getConfirmButton();
      const actions = Swal.getActions();

      if (cancelButton) {
        cancelButton.style.color = "#3f3f3f";
        cancelButton.style.border = "2px solid #90cdf4";
        cancelButton.style.boxShadow = "0 0 0 2px rgba(144, 205, 244, 0.35)";
      }

      if (actions && cancelButton && confirmButton) {
        actions.insertBefore(cancelButton, confirmButton);
      }
    },
  });

  return result.dismiss === Swal.DismissReason.cancel ? "excluir" : "continuar";
}

export function useMutationReserva({ pedido }: { pedido: Pedido | null}) {
  return useMutation({
     mutationFn: async (poltrona: PoltronaReserva) => {
      let url = '/passagens/reservar';

      if(pedido !== null && Object.prototype.hasOwnProperty.call(pedido, 'id')){
        url = `/passagens/pedido/${pedido.id}`;
      }

      const response = await api.post(url, {
        poltronas: [poltrona]
      });

      return response.data;
    },
      onError: (err: AxiosErrorResponse) => {
        const rawMessage = err.response?.data?.data?.message || "Não foi possível reservar a poltrona.";
        const messageWithoutRequestId = rawMessage.replace(/\s*-+\s*Request ID:.*/i, "").trim();

        const formattedHtml = (() => {
          const message = messageWithoutRequestId.replace(/^Aten[cç]ão!*\s*/i, "").trim();
          const parts: string[] = [];

          const serviceMatch = message.match(/^(\[[^\]]+\]:)/);
          let remainingMessage = message;

          if (serviceMatch) {
            parts.push(serviceMatch[1]);
            remainingMessage = remainingMessage.slice(serviceMatch[1].length).trim();
          }

          const [primary, followUp] = remainingMessage.split(/Por favor/i);
          if (primary?.trim()) parts.push(primary.trim());
          if (followUp?.trim()) parts.push(`Por favor ${followUp.trim()}`);

          return parts
            .filter(Boolean)
            .map((part) => `<div>${part}${/[.!?]$/.test(part) ? "" : "."}</div>`)
            .join("");
        })();
        
          Swal.fire({
            title: "Atenção!!!",
            icon: "warning",
            html: formattedHtml || messageWithoutRequestId,
          })

      },
  });
}
